(function () {
  "use strict";

  function pickStore(kind) {
    try {
      return kind === "session" ? window.sessionStorage : window.localStorage;
    } catch (e) {
      return null;
    }
  }

  /**
   * Throw-proof wrapper around localStorage / sessionStorage that transparently
   * JSON-encodes values. `read` returns `fallback` (default null) on any failure;
   * `write`/`remove` are no-ops that return false on failure (private mode, quota,
   * disabled storage).
   *
   * @param {'local'|'session'} kind - Which Web Storage to back the wrapper with.
   * @returns {{ available: function(): boolean, read: function(string, *=): *, write: function(string, *): boolean, remove: function(string): boolean }}
   *   A storage facade: `available()` probes writability, `read(key, fallback)`
   *   JSON-decodes a value (or returns `fallback`), `write(key, value)` JSON-encodes
   *   and stores it, `remove(key)` deletes it.
   * @example
   * const store = EckUtils.safeStorage('local');
   * const terms = store.read('eck_recent_searches', []);
   * store.write('eck_recent_searches', ['key fob']);
   */
  function safeStorage(kind) {
    const store = pickStore(kind);

    function available() {
      if (!store) return false;
      try {
        const probe = "__eck_probe__";
        store.setItem(probe, probe);
        store.removeItem(probe);
        return true;
      } catch (e) {
        return false;
      }
    }

    return {
      available,
      read: function (key, fallback) {
        const fb = fallback === undefined ? null : fallback;
        if (!store) return fb;
        try {
          const raw = store.getItem(key);
          return raw == null ? fb : JSON.parse(raw);
        } catch (e) {
          return fb;
        }
      },
      write: function (key, value) {
        if (!store) return false;
        try {
          store.setItem(key, JSON.stringify(value));
          return true;
        } catch (e) {
          return false;
        }
      },
      remove: function (key) {
        if (!store) return false;
        try {
          store.removeItem(key);
          return true;
        } catch (e) {
          return false;
        }
      },
    };
  }

  /**
   * Reveal-once IntersectionObserver: when `el` first intersects, run `cb` exactly
   * once and tear the observer down. With no IntersectionObserver support, runs `cb`
   * immediately (graceful degradation).
   *
   * @param {Element} el - The element to observe.
   * @param {function(): void} cb - Callback run once on first intersection (or immediately as a fallback).
   * @param {IntersectionObserverInit} [opts] - Observer options. Defaults to `{ rootMargin: '200px' }`.
   * @returns {IntersectionObserver|null} The observer (or null when unsupported), so
   *   callers can disconnect it in `disconnectedCallback` if needed.
   * @example
   * this.observer = EckUtils.revealOnIntersect(this, () => this.load());
   */
  function revealOnIntersect(el, cb, opts) {
    if (!("IntersectionObserver" in window)) {
      cb();
      return null;
    }
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            cb();
            observer.disconnect();
          }
        });
      },
      opts || { rootMargin: "200px" },
    );
    observer.observe(el);
    return observer;
  }

  /**
   * Fetch a section render, extract the `<componentName>` subtree's innerHTML, and
   * inject it into `targetEl` (only if the fetched subtree is non-empty). Mirrors
   * Dawn's ProductRecommendations.loadRecommendations (global.js:1153). Network/parse
   * failures are caught and logged, never thrown.
   *
   * @param {Element} targetEl - The element whose innerHTML is replaced with the fetched subtree.
   * @param {string} url - The section-render URL to fetch.
   * @param {string} componentName - Selector for the subtree to extract from the response (e.g. a custom-element tag name).
   * @returns {Promise<void>} Resolves after the injection attempt (and on caught failure).
   * @example
   * EckUtils.fetchSectionInto(this, this.dataset.url, 'eck-recently-viewed');
   */
  function fetchSectionInto(targetEl, url, componentName) {
    return fetch(url)
      .then(function (response) {
        return response.text();
      })
      .then(function (text) {
        const parsed = document.createElement("div");
        parsed.innerHTML = text;
        const fragment = parsed.querySelector(componentName);
        if (fragment && fragment.innerHTML.trim().length) {
          targetEl.innerHTML = fragment.innerHTML;
        }
      })
      .catch(function (e) {
        console.error(e);
      });
  }

  window.EckUtils = {
    safeStorage: safeStorage,
    revealOnIntersect: revealOnIntersect,
    fetchSectionInto: fetchSectionInto,
  };
})();
